/**
 * Phase 21.4 Batch 3C — 🏆 Admin · Contests list
 *
 * Landing page for the Contest Center. Lists every contest (draft +
 * published + closed + archived), lets admin filter by status, and
 * routes into the editor / entries / audit pages.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import SharedNavigation from "@/components/SharedNavigation";
import ContestFlagToggle from "@/components/contests/ContestFlagToggle";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_TONES = {
  draft: "bg-white/10 text-white/80",
  published: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  open: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  judging: "bg-amber-500/20 text-amber-200 border-amber-500/40",
  closed: "bg-purple-500/20 text-purple-200 border-purple-500/40",
  archived: "bg-white/5 text-gray-400",
};

// iter_211 — Persist filter/search/scroll for the Contest Center list so
// admins returning from the editor keep their exact context. Session
// storage (tab-scoped) — never survives a browser close, never leaks
// across accounts on a shared machine.
const LIST_STATE_KEY = "rgm.admin_contests.list_state";
const readListState = () => {
  try {
    const raw = sessionStorage.getItem(LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writeListState = (state) => {
  try { sessionStorage.setItem(LIST_STATE_KEY, JSON.stringify(state)); } catch (_e) { /* storage full or unavailable */ }
};

const AdminContestsPage = () => {
  const initialState = readListState() || {};
  const initialStateRef = useRef(initialState);   // iter_211 — frozen at mount so the restore effect isn't racing our own scroll listener
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(initialState.q || "");
  const [statusFilter, setStatusFilter] = useState(initialState.statusFilter || "all");
  const [loading, setLoading] = useState(true);
  const [flagEnabled, setFlagEnabled] = useState(null);
  const [savingFlag, setSavingFlag] = useState(false);
  const restoredScrollRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rowsRes, cfgRes] = await Promise.all([
        api.get("/admin/contests"),
        api.get("/homepage/config"),
      ]);
      setRows(rowsRes.data?.contests || []);
      setFlagEnabled(!!cfgRes.data?.contests?.enabled);
    } catch (e) {
      toast.error("Failed to load contests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // iter_211 — Persist filter/search + scroll position so the admin
  // returns to the exact list context after editing a contest.
  // The persistence effects intentionally SKIP the first render — the
  // very first mount has scrollY=0 (before restore fires) and running
  // it would clobber the saved scrollY. Use `didFilterMount` /
  // `didScrollMount` refs as first-run guards.
  const didFilterMountRef = useRef(false);
  useEffect(() => {
    if (!didFilterMountRef.current) {
      didFilterMountRef.current = true;
      return;
    }
    // Merge with any previously saved scrollY so a filter change
    // doesn't overwrite it.
    const prev = readListState() || {};
    writeListState({ ...prev, q, statusFilter });
  }, [q, statusFilter]);

  useEffect(() => {
    const saveScroll = () => {
      const prev = readListState() || {};
      writeListState({ ...prev, q, statusFilter, scrollY: window.scrollY });
    };
    // Debounced scroll listener so we always have a fresh position
    // written before the admin navigates away via a Link click.
    let t = null;
    const onScroll = () => {
      if (t) clearTimeout(t);
      t = setTimeout(saveScroll, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("beforeunload", saveScroll);
    return () => {
      saveScroll();
      if (t) clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("beforeunload", saveScroll);
    };
  }, [q, statusFilter]);

  // Restore scroll AFTER the table has rendered. `loading` flipping to
  // false is the reliable signal that rows are on the page. We use the
  // scrollY captured at mount (initialStateRef) so an automatic
  // scroll-to-top during navigation doesn't overwrite our target
  // before we can read it.
  useEffect(() => {
    if (loading || restoredScrollRef.current) return;
    const targetY = initialStateRef.current?.scrollY;
    if (typeof targetY === "number" && targetY > 0) {
      // rAF twice — first frame paints, second frame is safe to scroll.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => window.scrollTo(0, targetY));
      });
    }
    restoredScrollRef.current = true;
  }, [loading]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (query) {
        return (
          (c.title || "").toLowerCase().includes(query) ||
          (c.slug || "").toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [rows, q, statusFilter]);

  const publish = async (contestId) => {
    try {
      await api.post(`/admin/contests/${contestId}/publish`);
      toast.success("Contest published — status is now OPEN");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };
  const close = async (contestId) => {
    if (!window.confirm("Close this contest? Entries will be frozen.")) return;
    try {
      await api.post(`/admin/contests/${contestId}/close`);
      toast.success("Contest closed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed");
    }
  };
  // Reuses the canonical PATCH /admin/contests/{id} status transition
  // (contest_routes.py line 1056). Backend already fires the
  // `contest_judging` fanout to entrants when prev_status != "judging"
  // and new status == "judging". No new endpoint / status value / notification.
  const beginJudging = async (contestId) => {
    if (!window.confirm("Move this contest into JUDGING? Judges can begin scoring; new entries are no longer part of the OPEN phase.")) return;
    try {
      await api.patch(`/admin/contests/${contestId}`, { status: "judging" });
      toast.success("Contest moved to JUDGING");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Begin Judging failed");
    }
  };
  const del = async (contestId) => {
    if (!window.confirm("Delete this draft contest permanently?")) return;
    try {
      await api.delete(`/admin/contests/${contestId}`);
      toast.success("Draft deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed (only drafts can be deleted)");
    }
  };
  // iter_184 (P0) — Force-regenerate the 1500×1500 shareable winner
  // covers for one contest. Safe to run anytime — always overwrites.
  const [regenerating, setRegenerating] = useState(null); // contest_id
  const regenCovers = async (contestId) => {
    setRegenerating(contestId);
    try {
      const { data } = await api.post(`/admin/contests/${contestId}/regenerate-covers`);
      toast.success(
        `🎨 ${data.repaired || 0}/${data.scanned || 0} covers regenerated` +
        (data.errors?.length ? ` (${data.errors.length} errors)` : "")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegenerating(null);
    }
  };
  const [backfillingAll, setBackfillingAll] = useState(false);
  const backfillAllMissingCovers = async () => {
    if (!window.confirm("Backfill covers for ALL contests missing them? (safe, idempotent)")) return;
    setBackfillingAll(true);
    try {
      const { data } = await api.post("/admin/contests/backfill/covers", { dry_run: false });
      toast.success(
        `🎨 Backfill complete: ${data.repaired || 0} generated, ${data.skipped || 0} already had covers` +
        (data.errors?.length ? `, ${data.errors.length} errors` : "")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillingAll(false);
    }
  };
  const toggleFlag = async () => {
    setSavingFlag(true);
    try {
      // Direct write via a helper admin endpoint would be cleaner; for
      // now we mutate through platform_settings. Contest Center exposes
      // no dedicated flag endpoint yet — using the singleton config
      // pattern requires an admin route. Skip if not present.
      const next = !flagEnabled;
      // Emergency path — no dedicated endpoint yet: do it via generic put pattern later
      // For now, disable this toggle UI-side to avoid confusion.
      toast.info(next ? "Flip flag from backend (no admin endpoint yet)" : "Flip flag from backend (no admin endpoint yet)");
    } finally {
      setSavingFlag(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-contests-page">
      <SharedNavigation />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-black">🏆 Contest Center</h1>
            <p className="text-sm text-gray-400">Create, publish, judge and award contests.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <ContestFlagToggle />
            <Link
              to="/admin/badge-catalog"
              className="text-xs text-purple-300 hover:text-purple-200 underline"
              data-testid="admin-contests-open-badge-catalog"
            >
              Badge catalog →
            </Link>
            <Link
              to="/admin/hall-of-champions"
              className="text-xs text-amber-300 hover:text-amber-200 underline"
              data-testid="admin-contests-open-hof"
            >
              Hall of Champions →
            </Link>
            <Link
              to="/admin/contests/new"
              className="inline-flex items-center px-4 py-2 rounded-full bg-white text-black font-black text-sm hover:bg-amber-200"
              data-testid="admin-contests-new-btn"
            >
              + New Contest
            </Link>
            <Button
              onClick={backfillAllMissingCovers}
              disabled={backfillingAll}
              className="bg-amber-500 hover:bg-amber-400 text-black font-black text-sm"
              data-testid="admin-contests-backfill-all-covers"
            >
              {backfillingAll ? "…" : "🎨 Backfill All Missing Covers"}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title or slug"
            className="max-w-xs bg-white/5 border-white/10 text-white"
            data-testid="admin-contests-search"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm"
            data-testid="admin-contests-status-filter"
          >
            {["all", "draft", "published", "open", "judging", "closed", "archived"].map((s) => (
              <option key={s} value={s} className="bg-[#13131a]">{s}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-gray-500 text-sm" data-testid="admin-contests-loading">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-500 text-sm" data-testid="admin-contests-empty">No contests yet — click &quot;New Contest&quot;.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-contests-table">
              <thead className="text-left text-gray-400 text-xs uppercase tracking-wider border-b border-white/10">
                <tr>
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Slug</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Deadline</th>
                  <th className="py-2 pr-3">Winners</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.contest_id}
                    className="border-b border-white/5"
                    data-testid={`admin-contest-row-${c.contest_id}`}
                  >
                    <td className="py-2 pr-3 text-white font-semibold">{c.title}</td>
                    <td className="py-2 pr-3 text-gray-400 font-mono text-xs">{c.slug}</td>
                    <td className="py-2 pr-3 text-gray-300">{c.entry_type}</td>
                    <td className="py-2 pr-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase font-black border ${STATUS_TONES[c.status] || "bg-white/5 text-gray-300"}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-gray-400 text-xs">{c.submission_deadline ? new Date(c.submission_deadline).toLocaleString() : "—"}</td>
                    <td className="py-2 pr-3 text-gray-300">{c.winner_slots}</td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1.5">
                        <Link
                          to={`/admin/contests/${c.contest_id}`}
                          className="text-xs text-purple-300 hover:underline"
                          data-testid={`admin-contest-edit-${c.contest_id}`}
                        >Edit</Link>
                        <Link
                          to={`/admin/contests/${c.contest_id}/entries`}
                          className="text-xs text-cyan-300 hover:underline"
                          data-testid={`admin-contest-entries-${c.contest_id}`}
                        >Entries</Link>
                        <Link
                          to={`/admin/contests/${c.contest_id}/judging`}
                          className="text-xs px-2 py-0.5 rounded border border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-500/10 font-bold"
                          data-testid={`admin-contest-judging-${c.contest_id}`}
                        >⚖️ Manage Judges</Link>
                        {c.status === "draft" && (
                          <button
                            onClick={() => publish(c.contest_id)}
                            className="text-xs text-emerald-300 hover:underline"
                            data-testid={`admin-contest-publish-${c.contest_id}`}
                          >Publish</button>
                        )}
                        {["open", "published"].includes(c.status) && (
                          <button
                            onClick={() => beginJudging(c.contest_id)}
                            className="text-xs text-fuchsia-300 hover:underline"
                            data-testid={`admin-contest-begin-judging-${c.contest_id}`}
                          >Begin Judging</button>
                        )}
                        {["open", "published", "judging"].includes(c.status) && (
                          <button
                            onClick={() => close(c.contest_id)}
                            className="text-xs text-amber-300 hover:underline"
                            data-testid={`admin-contest-close-${c.contest_id}`}
                          >Close</button>
                        )}
                        {c.status === "draft" && (
                          <button
                            onClick={() => del(c.contest_id)}
                            className="text-xs text-rose-300 hover:underline"
                            data-testid={`admin-contest-delete-${c.contest_id}`}
                          >Delete</button>
                        )}
                        {["closed", "archived"].includes(c.status) && (
                          <button
                            onClick={() => regenCovers(c.contest_id)}
                            disabled={regenerating === c.contest_id}
                            className="text-xs text-amber-300 hover:underline disabled:opacity-50"
                            data-testid={`admin-contest-regen-covers-${c.contest_id}`}
                            title="Regenerate 1500×1500 winner covers"
                          >
                            {regenerating === c.contest_id ? "…" : "🎨 Regen Covers"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContestsPage;
